import React, { useState, useEffect } from 'react';
import { Autocomplete, Button, Card, CardContent, CardHeader, Typography, Box, ListItemText, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, CircularProgress, Switch, FormControlLabel, Chip, Pagination } from "@mui/material";
import { Edit, Delete } from '@mui/icons-material';
import { TabPanel } from '@mui/lab';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { getCharges, createCharge, deleteCharge, updateCharge } from '../redux/ducks/charges';
import { getReportClients } from "../redux/ducks/reports";
import { getClientAccounts } from "../redux/ducks/client";
import AppDatePicker from "./AppDatePicker";
import { formatDate, toLocalDay } from "../utils/dateUtils";

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  alignItems: "stretch",
  padding: theme.spacing(3, 0, 0, 0),
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
}));

const ChargesCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(1),
  width: "100%",
  [theme.breakpoints.up("md")]: {
    width: "50%",
  },
}));

const ChargesBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const ChargesLine = styled(Box, { shouldForwardProp: (prop) => prop !== 'active' })(({ theme, active }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  border: `1px solid ${active === 'true' ? theme.palette.divider : theme.palette.error.main}`,
  borderRadius: theme.shape.borderRadius,
  opacity: active === 'true' ? 1 : 0.4,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
    padding: theme.spacing(1.5),
  },
}));

const ChargesLineContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    justifyContent: "space-between",
    marginTop: theme.spacing(1),
  },
}));

const PaginationRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
}));

const ClientSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(2),
  alignItems: "center",
}));

const HalfWidthAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: "50%",
}));

const DisabledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
}));


const BillingCharges = ({ tabValue }) => {
    const thisTab = "charges";
    const [charges, setCharges] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [staticCharges, setStaticCharges] = useState([]);
    const [recurringCharges, setRecurringCharges] = useState([]);
    const [staticPage, setStaticPage] = useState(1);
    const [recurringPage, setRecurringPage] = useState(1);
    const pageSize = 10;
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [chargeToDelete, setChargeToDelete] = useState({});
    const [chargeToAdd, setChargeToAdd] = useState({});
    const [client, setClient] = useState(null);
    const [clients, setClients] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const dispatch = useDispatch();

    const validateCharge = (c) => {
      const errors = {};
      if (!c?.name || !String(c.name).trim()) {
        errors.name = "Name is required";
      }
      if (!c?.description || !String(c.description).trim()) {
        errors.description = "Description is required";
      }
      const amountNum = Number(c?.amount);
      if (c?.amount === "" || c?.amount === undefined || c?.amount === null || Number.isNaN(amountNum) || amountNum <= 0) {
        errors.amount = "Amount must be greater than 0";
      }
      // Any valid date string is acceptable — backdating allowed by design.
      if (!c?.date || Number.isNaN(new Date(c.date).getTime())) {
        errors.date = "Valid date required";
      }
      if (!c?.client || typeof c.client !== "string" || !c.client.trim()) {
        errors.client = "Client is required";
      }
      if (!c?.account || typeof c.account !== "string" || !c.account.trim()) {
        errors.account = "Account is required";
      }
      return errors;
    };

    useEffect(() => {
      if (tabValue === thisTab) {
        dispatch(getCharges((response) => {
          if (response && Array.isArray(response.charges)) {
            setCharges(response.charges);
          }
        }));
      }
    }, [dispatch, tabValue]);

    useEffect(() => {
      setIsLoading(true);
      setStaticCharges(charges.filter((charge) => charge.recurring === false));
      setRecurringCharges(charges.filter((charge) => charge.recurring === true));
      setStaticPage(1);
      setRecurringPage(1);
      setIsLoading(false);
    }, [charges]);

    const staticPageCount = Math.max(1, Math.ceil(staticCharges.length / pageSize));
    const recurringPageCount = Math.max(1, Math.ceil(recurringCharges.length / pageSize));
    const visibleStatic = staticCharges.slice((staticPage - 1) * pageSize, staticPage * pageSize);
    const visibleRecurring = recurringCharges.slice((recurringPage - 1) * pageSize, recurringPage * pageSize);

    useEffect(() => {
      if (isAdding || isEditing) {
        dispatch(getReportClients((response) => {
          setClients(response.filter((client) => client.active === true));
        }));
      }
    }, [isAdding, isEditing, dispatch]);

    useEffect(() => {
      if (!client) {
        setAccounts([]);
        return;
      }
      dispatch(getClientAccounts(client, (response) => {
        setAccounts(response || []);
      }));
    }, [dispatch, client]);

    const handleDeleteDialogClose = () => {
      setDeleteDialogOpen(false);
      setChargeToDelete(null);
    };

    const handleEditCharge = (charge) => {
      setIsEditing(true);
      setClient(charge.client?._id);
      setChargeToAdd({ ...charge, client: charge.client?._id, account: charge.account?._id, date: formatDate(charge.date) });
    };

    const confirmDeleteCharge = () => {
      dispatch(deleteCharge(chargeToDelete._id, (response) => {
        if (response.status === 1) {
          setCharges(charges.filter((charge) => charge._id !== chargeToDelete._id));
          setDeleteDialogOpen(false);
          setChargeToDelete(null);
        }
      }));
    };

    const handleDeleteCharge = (charge) => {
      setDeleteDialogOpen(true);
      setChargeToDelete(charge);
    };

    const handleCancelAddCharge = () => {
      setIsAdding(false);
      setIsEditing(false);
      setChargeToAdd({
        recurring: false,
        active: true
      });
    };

    const handleAddStaticCharge = (charge) => {
      setIsAdding(true);
      setChargeToAdd({
        recurring: false,
        currency: "USD",
        date: toLocalDay(),
        interval: "Month",
        active: true
      });
    };

    const handleAddRecurringCharge = (charge) => {
      setIsAdding(true);
      setChargeToAdd({
        recurring: true,
        currency: "USD",
        date: toLocalDay(),
        interval: "Month",
        active: true
      });
    };

    const handleAddCharge = () => {
      if (chargeToAdd._id) {
        dispatch(updateCharge(chargeToAdd._id, chargeToAdd, (response) => {
          if (response) {
            setIsEditing(false);
            setChargeToAdd({});
            setClient(null);
            setCharges(charges.map((charge) => charge._id === chargeToAdd._id ? response.charge : charge));
          }
        }))
      } else {
        dispatch(createCharge(chargeToAdd, (response) => {
          if (response) {
            setIsAdding(false);
            setChargeToAdd({});
            setClient(null);
            setCharges([...charges, response.charge]);
          }
        }))
      }
    };

    const chargeErrors = validateCharge(chargeToAdd);
    const isChargeValid = Object.keys(chargeErrors).length === 0;

    return (
      <>
        <StyledTabPanel value={thisTab}>
          <ChargesCard>
            <CardHeader
              title="Static Charges"
              subheader="Configure static charges for clients"
              action={
                <Button variant="outlined" fullWidth onClick={handleAddStaticCharge}>
                  Add Static Charge
                </Button>
              }
            />
            <CardContent>
              <ChargesBox>
                {isLoading ? (
                  <CircularProgress />
                ) : staticCharges.length > 0 ? (
                  visibleStatic.map((charge) => (
                    <ChargesLine key={charge?.id} active={charge?.active?.toString()}>
                      {charge?.active ? (
                        <ClientSection>
                          <ListItemText primary={charge?.client?.name} secondary={charge?.description} />
                          <Chip label={charge?.billingStatus} size="small" />
                        </ClientSection>
                      ) : (
                        <ClientSection>
                          <ListItemText primary={charge?.client?.name} secondary={charge?.description} />
                          <DisabledChip label="Disabled" size="small" />
                        </ClientSection>
                      )}
                      <ChargesLineContent>
                        
                        <ListItemText primary={`${charge?.amount.toFixed(2)} ${charge?.currency}`} secondary={charge?.interval} />
                        <Box>
                          <IconButton onClick={() => handleEditCharge(charge)}>
                            <Edit />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteCharge(charge)}>
                            <Delete />
                          </IconButton>
                        </Box>
                      </ChargesLineContent>
                    </ChargesLine>
                  ))
                ) : (
                  <Typography>No static charges found</Typography>
                )}
              </ChargesBox>
              {staticPageCount > 1 && (
                <PaginationRow>
                  <Pagination count={staticPageCount} page={staticPage} onChange={(_, v) => setStaticPage(v)} size="small" color="primary" />
                </PaginationRow>
              )}
            </CardContent>
          </ChargesCard>

          <ChargesCard>
            <CardHeader
              title="Recurring Charges"
              subheader="Configure recurring charges for clients"
              action={
                <Button variant="outlined" fullWidth onClick={handleAddRecurringCharge}>
                  Add Recurring Charge
                </Button>
              }
            />
            <CardContent>
              <ChargesBox>
                {recurringCharges.length > 0 &&
                  visibleRecurring.map((charge) => (
                    <ChargesLine key={charge?.id} active={charge?.active?.toString()}>
                      {charge?.active ? (
                        <ListItemText primary={charge?.client?.name} secondary={charge?.description} />
                      ) : (
                        <ClientSection>
                          <ListItemText primary={charge?.client?.name} secondary={charge?.description} />
                          <DisabledChip label="Disabled" size="small" />
                        </ClientSection>
                      )}
                      <ChargesLineContent>
                        <ListItemText primary={`${charge?.amount.toFixed(2)} ${charge?.currency}`} secondary={charge?.interval} />
                        <Box>
                          <IconButton onClick={() => handleEditCharge(charge)}>
                            <Edit />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteCharge(charge)}>
                            <Delete />
                          </IconButton>
                        </Box>
                      </ChargesLineContent>
                    </ChargesLine>
                  ))}
              </ChargesBox>
              {recurringPageCount > 1 && (
                <PaginationRow>
                  <Pagination count={recurringPageCount} page={recurringPage} onChange={(_, v) => setRecurringPage(v)} size="small" color="primary" />
                </PaginationRow>
              )}
            </CardContent>
          </ChargesCard>
        </StyledTabPanel>

        <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
          <DialogTitle>Delete Charge</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this charge?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteDialogClose}>Cancel</Button>
            <Button onClick={confirmDeleteCharge} variant="contained" color="primary">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={isAdding || isEditing} fullWidth onClose={handleCancelAddCharge} disableRestoreFocus>
          <DialogTitle>{isAdding ? "Add Charge" : "Edit Charge"}</DialogTitle>
          <StyledDialogContent dividers>
            <ClientSection>
              <HalfWidthAutocomplete
                options={clients}
                getOptionLabel={(option) => option?.name || ''}
                value={clients.find((c) => c.id === chargeToAdd?.client) || null}
                onChange={(_, newValue) => {
                  const id = newValue?.id || '';
                  setClient(id);
                  setChargeToAdd({ ...chargeToAdd, client: id });
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Client" fullWidth error={Boolean(chargeErrors.client)} helperText={chargeErrors.client || ''} />}
              />
              <HalfWidthAutocomplete
                options={accounts}
                getOptionLabel={(option) => option?.name || ''}
                value={accounts.find((a) => a.id === chargeToAdd?.account) || null}
                onChange={(_, newValue) => setChargeToAdd({ ...chargeToAdd, account: newValue?.id || '' })}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Account" fullWidth error={Boolean(chargeErrors.account)} helperText={chargeErrors.account || ''} />}
              />
            </ClientSection>
            <TextField label="Name" value={chargeToAdd?.name || ''} onChange={(e) => setChargeToAdd({ ...chargeToAdd, name: e.target.value })} error={Boolean(chargeErrors.name)} helperText={chargeErrors.name || ''} />
            <TextField label="Description" value={chargeToAdd?.description || ''} onChange={(e) => setChargeToAdd({ ...chargeToAdd, description: e.target.value })} error={Boolean(chargeErrors.description)} helperText={chargeErrors.description || ''} />
            <TextField label="Amount" type="number" inputProps={{ min: 0, step: "0.01" }} value={chargeToAdd?.amount ?? ''} onChange={(e) => setChargeToAdd({ ...chargeToAdd, amount: e.target.value })} error={Boolean(chargeErrors.amount)} helperText={chargeErrors.amount || ''} />
            <TextField label="Currency" value={chargeToAdd?.currency || "USD"} onChange={(e) => setChargeToAdd({ ...chargeToAdd, currency: e.target.value })} select>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="GBP">GBP</MenuItem>
              <MenuItem value="CAD">CAD</MenuItem>
              <MenuItem value="AUD">AUD</MenuItem>
            </TextField>
            <AppDatePicker label="Date" value={chargeToAdd?.date || toLocalDay()} onChange={(v) => setChargeToAdd({ ...chargeToAdd, date: v })} error={Boolean(chargeErrors.date)} helperText={chargeErrors.date || ''} fullWidth={false} size="medium" />
            <ClientSection>
              <FormControlLabel label="Recurring" control={<Switch checked={chargeToAdd?.recurring || false} onChange={(e) => setChargeToAdd({ ...chargeToAdd, recurring: e.target.checked })} />} />
              {chargeToAdd?.recurring && (
                <TextField label="Frequency" value={chargeToAdd?.interval || "Month"} onChange={(e) => setChargeToAdd({ ...chargeToAdd, interval: e.target.value })} select>
                  <MenuItem value="Week">Weekly</MenuItem>
                  <MenuItem value="Month">Monthly</MenuItem>
                  <MenuItem value="Year">Yearly</MenuItem>
                </TextField>
              )}
            </ClientSection>
            <FormControlLabel label="Active" control={<Switch checked={chargeToAdd?.active ?? true} onChange={(e) => setChargeToAdd({ ...chargeToAdd, active: e.target.checked })} />} />
          </StyledDialogContent>
          <DialogActions>
            <Button onClick={handleCancelAddCharge}>Cancel</Button>
            <Button onClick={handleAddCharge} variant="contained" color="primary" disabled={!isChargeValid}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
}

export default BillingCharges;